<template>
  <StickyListHeader
    document-class="approvals-dashboard"
    :header-columns="headerColumns"
    selected-sort=""
    sort-direction="desc"
    top-offset="304px"
  />
  <ListFilterEmptyState
    v-if="showEmptyState"
    message="No approval flows match your filters or search. Try adjusting your filters or search term."
  />
  <LibraryList v-else>
    <LibraryListItemWrapper
      v-for="flow in flows"
      :key="flow.ulid"
      class="approvals-library-list-item-wrapper"
      :class="{ 'approvals-library-list-item-selected': isFlowSelected(flow.ulid) }"
    >
      <button
        type="button"
        class="approvals-library-list-link"
        @click="handleRowClick(flow)"
      >
        <ApprovalsLibraryListItem
          class="approvals-library-list-item"
          :flow="flow"
        />
      </button>
    </LibraryListItemWrapper>
  </LibraryList>

  <StickyPagination
    v-if="totalPages > 1"
    :model-value="page"
    :length="totalPages"
    @update:model-value="(value) => sendFilterRequest({
      pageNumber: value,
      router,
    })"
  />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import { useApprovalsDashboardStore } from '@/stores/jobProfiles/approvalsDashboard';
import ApprovalsLibraryListItem from '@/components/library/approvals/ApprovalsLibraryListItem.vue';
import ListFilterEmptyState from '@/components/shared/ListFilterEmptyState.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import { useFilters } from '@/stores/filters/filters';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import { useAppPagination } from '@/stores/app/pagination';
import { stickyHeadersApprovals } from '@/helpers/constants/stickyHeaders';
import { getTooltip } from '@/helpers/tooltips';

const route = useRoute();
const router = useRouter();

const { totalPages, page } = storeToRefs(useAppPagination());
const { areFiltersApplied } = storeToRefs(useFilters());
const { flows } = storeToRefs(useApprovalsDashboardStore());

const { sendFilterRequest } = useFilters();

const hasFiltersOrSearch = computed(() => {
  const hasSearch = Boolean(route.query?.q);

  return hasSearch || areFiltersApplied.value;
});

const showEmptyState = computed(() => (
  flows.value.length === 0
  && hasFiltersOrSearch.value
));

const headerColumns = computed(() => {
  const baseColumns = [
    stickyHeadersApprovals.jobProfile,
    stickyHeadersApprovals.status,
    stickyHeadersApprovals.stages,
    stickyHeadersApprovals.createdBy,
    stickyHeadersApprovals.days,
    stickyHeadersApprovals.actions,
  ];

  return baseColumns.map((column) => {
    if (column.tooltip?.key) {
      const tooltip = getTooltip(column.tooltip.key);

      return {
        ...column,
        tooltip: tooltip.html ? tooltip : null,
      };
    }

    return column;
  });
});

const handleRowClick = (flow) => {
  router.replace({
    query: {
      ...route.query,
      preview: flow.ulid,
      type: 'approvalFlow',
    },
  });
};

const isFlowSelected = (flowUlid) => (
  route.query?.type === 'approvalFlow' && route.query?.preview === flowUlid
);
</script>

<style lang="less" scoped>
:deep(.sticky-header-items) {
  height: 100%;
}

:deep(.document-list-item-wrapper):not(:last-child) {
  border-bottom: 1px solid @stroke-default;
}

.approvals-library-list-link {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

:deep(.approvals-library-list-item-selected) {
  background: @bg-canvas;
}

.approvals-library-list-item,
:deep(.sticky-header-items.approvals-dashboard) {
  grid-template-columns:
    minmax(140px, 1.6fr)
    minmax(100px, 0.9fr)
    minmax(80px, 0.6fr)
    minmax(120px, 1fr)
    minmax(60px, 0.5fr)
    minmax(100px, 0.8fr);
  height: 100%;
  gap: 0;
}

:deep(.sticky-header-items.approvals-dashboard) {
  .job-profile,
  .status,
  .stages,
  .created-by,
  .days,
  .actions {
    justify-self: start;
    padding-left: 16px;
  }
}

:deep(.sticky-header-items.approvals-dashboard .sortable-column) {
  width: 100%;

  p {
    font-size: 12px !important;
    font-weight: 500 !important;
    line-height: 150% !important;
  }

  &.job-profile,
  &.status,
  &.stages,
  &.created-by,
  &.days,
  &.actions {
    justify-content: flex-start;
  }
}
</style>
